import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  SHELL_RUNTIME_OPEN_NOTIFICATIONS_DRAWER_EVENT,
} from '@shared/host/shell-runtime';
import {
  NotificationBell,
  NotificationDrawer,
  useNotificationCenterController,
  useResolvedNotifications,
  type NotificationViewItem,
} from '@workspace/ui';
import type {
  NotificationMetadataBase,
  NotificationsSnapshot,
} from '@shared/notifications/shell-notifications-contract';
import {
  resolveShellNotificationDestination,
  shellNotificationsClient,
  useShellNotifications,
} from '../../infrastructure/notifications';
import { useAuthStore } from '../../state/auth-store';

const EMPTY_NOTIFICATIONS_SNAPSHOT: NotificationsSnapshot<NotificationMetadataBase> =
  Object.freeze({
    items: [],
    unreadCount: 0,
    totalCount: 0,
    isLoading: false,
    isFetching: false,
    error: null,
    version: 0,
  });

export function ShellNotificationsMenu() {
  const navigate = useNavigate();
  const snapshot = useShellNotifications() ?? EMPTY_NOTIFICATIONS_SNAPSHOT;
  const user = useAuthStore((state) => state.user);
  const { t, i18n } = useTranslation(["layout", "notifications"] as const);

  const translate = useCallback(
    (key: string, options?: Record<string, unknown>) => {
      const translated = t(key, options);
      return typeof translated === "string" ? translated : String(translated);
    },
    [t],
  );

  const notifications = useResolvedNotifications({
    items: snapshot.items,
    totalCount: snapshot.unreadCount,
    isLoading: snapshot.isLoading,
    isFetching: snapshot.isFetching,
    error: snapshot.error,
    refetch: () => shellNotificationsClient.refresh("manual"),
    acknowledge: async (id: string) => {
      await shellNotificationsClient.acknowledge(id);
    },
    deleteNotification: async (id: string) => {
      await shellNotificationsClient.dismiss(id);
    },
    resolverContext: {
      isAdmin: user?.role === "ADMIN" || user?.role === "MANAGER",
    },
  });

  const handleNavigateNotification = useCallback(
    async (notification: NotificationViewItem) => {
      const destination = resolveShellNotificationDestination(notification);

      if (!notification.isRead) {
        try {
          await notifications.acknowledge(notification.id);
        } catch (error) {
          console.warn(
            "[shell-notifications] Failed to acknowledge notification before navigation",
            error,
          );
        }
      }

      navigate(destination.to, {
        replace: destination.replace,
        state: destination.state,
      });
    },
    [navigate, notifications],
  );

  const handleDismissNotification = useCallback(
    async (notification: NotificationViewItem) => {
      try {
        await notifications.deleteNotification(notification.id);
      } catch (error) {
        console.warn("[shell-notifications] Failed to dismiss notification", error);
      }
    },
    [notifications],
  );

  const controller = useNotificationCenterController({
    notifications,
    i18n,
    translate,
    onNavigate: handleNavigateNotification,
    onDelete: handleDismissNotification,
  });
  const openDrawer = controller.contextValue.openDrawer;

  useEffect(() => {
    const handleOpenDrawer = () => {
      openDrawer();
    };

    window.addEventListener(
      SHELL_RUNTIME_OPEN_NOTIFICATIONS_DRAWER_EVENT,
      handleOpenDrawer as EventListener,
    );

    return () => {
      window.removeEventListener(
        SHELL_RUNTIME_OPEN_NOTIFICATIONS_DRAWER_EVENT,
        handleOpenDrawer as EventListener,
      );
    };
  }, [openDrawer]);

  return (
    <>
      <div className="shell-notifications-trigger">
        <NotificationBell
          count={controller.contextValue.totalCount}
          isFetching={controller.contextValue.isFetching}
          label={controller.contextValue.label}
          isOpen={controller.contextValue.isOpen}
          onClick={controller.contextValue.toggleDrawer}
        />
      </div>

      <NotificationDrawer
        isOpen={controller.isOpen}
        onClose={controller.closeDrawer}
        title={controller.title}
        emptyTitle={controller.emptyTitle}
        emptySubtitle={controller.emptySubtitle}
        retryLabel={controller.retryLabel}
        dismissLabel={controller.dismissLabel}
        categoryLabels={controller.categoryLabels}
        items={controller.items}
        isLoading={controller.isLoading}
        errorMessage={
          controller.error
            ? controller.error.message || translate("topbar.notifications.error")
            : null
        }
        isUpdatingNotification={controller.isUpdatingNotification}
        getTimestampLabel={controller.getTimestampLabel}
        resolveCopy={controller.resolveCopy}
        onNavigate={controller.handleNavigate}
        onDelete={controller.handleDelete}
        onRetry={controller.refetch}
      />
    </>
  );
}
